import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as Linking from 'expo-linking';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, ArrowSquareOut, BookOpen, Check, CloudCheck, FileText, WifiSlash } from 'phosphor-react-native';
import { useEffect, useState } from 'react';
import { Dimensions, Image, Modal, RefreshControl, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width, height } = Dimensions.get('window');

// ============================================
// 📝 TYPES
// ============================================

interface ExamenItem {
  id: string;
  title: string;
  matiere: string;
  year: number;
  pdf_url: string | null;
  corrige_url: string | null;
  ordre: number;
  is_free: boolean;
}

// ============================================
// 📦 SYSTÈME DE CACHE
// ============================================

const CACHE_PREFIX = 'examen_cache_';
const CACHE_META_PREFIX = 'examen_meta_';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours

interface CachedExamen {
  content: string;
  timestamp: number;
}

interface CachedExamenMeta {
  data: ExamenItem;
  timestamp: number;
}

const getCachedExamen = async (examenId: string): Promise<string | null> => {
  try {
    const cached = await AsyncStorage.getItem(`${CACHE_PREFIX}${examenId}`);
    if (!cached) return null;
    
    const { content, timestamp }: CachedExamen = JSON.parse(cached);
    
    if (Date.now() - timestamp > CACHE_DURATION) {
      await AsyncStorage.removeItem(`${CACHE_PREFIX}${examenId}`);
      return null;
    }
    
    return content;
  } catch (error) {
    console.error('Erreur lecture cache:', error);
    return null;
  }
};

const setCachedExamen = async (examenId: string, content: string): Promise<void> => {
  try {
    const cacheData: CachedExamen = {
      content,
      timestamp: Date.now(),
    };
    await AsyncStorage.setItem(`${CACHE_PREFIX}${examenId}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Erreur écriture cache:', error);
  }
};

const getCachedExamenMeta = async (examenId: string): Promise<ExamenItem | null> => {
  try {
    const cached = await AsyncStorage.getItem(`${CACHE_META_PREFIX}${examenId}`);
    if (!cached) return null;
    
    const { data, timestamp }: CachedExamenMeta = JSON.parse(cached);
    
    // Le cache meta expire aussi après 7 jours
    if (Date.now() - timestamp > CACHE_DURATION) {
      return null;
    }
    
    return data;
  } catch (error) {
    return null;
  }
};

const setCachedExamenMeta = async (examenId: string, data: ExamenItem): Promise<void> => {
  try {
    const cacheData: CachedExamenMeta = {
      data,
      timestamp: Date.now(),
    };
    await AsyncStorage.setItem(`${CACHE_META_PREFIX}${examenId}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Erreur écriture cache meta:', error);
  }
};

// Design System Colors (même que les fiches - violet/indigo)
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  purple600: '#7C3AED',
  purple100: '#EDE9FE',
};

// ============================================
// 🎨 PARSER MARKDOWN (même que fiches)
// ============================================

interface ParsedElement {
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'quote' | 'table' | 'image' | 'hr' | 'list' | 'numbered-list';
  content: string;
  rows?: string[][];
  items?: string[];
}

const parseMarkdown = (markdown: string | null | undefined): ParsedElement[] => {
  if (!markdown || typeof markdown !== 'string') {
    return [];
  }

  const lines = markdown.split('\n');
  const elements: ParsedElement[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (trimmedLine === '') {
      i++;
      continue;
    }

    if (trimmedLine === '---') {
      elements.push({ type: 'hr', content: '' });
      i++;
      continue;
    }

    if (trimmedLine.startsWith('# ')) {
      elements.push({ type: 'h1', content: trimmedLine.substring(2) });
      i++;
      continue;
    }
    if (trimmedLine.startsWith('## ')) {
      elements.push({ type: 'h2', content: trimmedLine.substring(3) });
      i++;
      continue;
    }
    if (trimmedLine.startsWith('### ')) {
      elements.push({ type: 'h3', content: trimmedLine.substring(4) });
      i++;
      continue;
    }

    if (trimmedLine.startsWith('> ')) {
      let quoteContent = trimmedLine.substring(2);
      i++;
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteContent += ' ' + lines[i].trim().substring(2);
        i++;
      }
      elements.push({ type: 'quote', content: quoteContent });
      continue;
    }

    if (trimmedLine.startsWith('![')) {
      const match = trimmedLine.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (match) {
        elements.push({ type: 'image', content: match[2] });
      }
      i++;
      continue;
    }

    if (trimmedLine.startsWith('|')) {
      const tableRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const row = lines[i].trim();
        if (!row.match(/^\|[\s-:|]+\|$/)) {
          const cells = row.split('|').filter(c => c.trim() !== '').map(c => c.trim());
          tableRows.push(cells);
        }
        i++;
      }
      if (tableRows.length > 0) {
        elements.push({ type: 'table', content: '', rows: tableRows });
      }
      continue;
    }

    if (/^\d+\.\s/.test(trimmedLine)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push({ type: 'numbered-list', content: '', items });
      continue;
    }

    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))) {
        items.push(lines[i].trim().substring(2));
        i++;
      }
      elements.push({ type: 'list', content: '', items });
      continue;
    }

    let paragraphContent = trimmedLine;
    i++;
    while (
      i < lines.length && 
      lines[i].trim() !== '' && 
      !lines[i].trim().startsWith('#') &&
      !lines[i].trim().startsWith('>') &&
      !lines[i].trim().startsWith('|') &&
      !lines[i].trim().startsWith('-') &&
      !lines[i].trim().startsWith('*') &&
      !lines[i].trim().startsWith('![') &&
      !lines[i].trim().match(/^\d+\./) &&
      lines[i].trim() !== '---'
    ) {
      paragraphContent += ' ' + lines[i].trim();
      i++;
    }
    elements.push({ type: 'paragraph', content: paragraphContent });
  }

  return elements;
};

// ============================================
// 🔤 FORMATER LE TEXTE
// ============================================

interface TextPart {
  text: string;
  bold?: boolean;
}

const formatText = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index) });
    }
    parts.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex) });
  }

  if (parts.length === 0) {
    parts.push({ text });
  }

  return parts;
};

// ============================================
// 📄 COMPOSANT PRINCIPAL
// ============================================

export default function ExamenViewerScreen() {
  const { id } = useLocalSearchParams();
  const [examen, setExamen] = useState<ExamenItem | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useEffect(() => {
    loadExamenFromSupabase();
  }, [id]);

  const loadExamenFromSupabase = async () => {
    try {
      setError(null);

      // 1. Essayer de charger depuis le cache IMMÉDIATEMENT (métadonnées + contenu)
      const [cachedMeta, cachedContent] = await Promise.all([
        getCachedExamenMeta(id as string),
        getCachedExamen(id as string),
      ]);

      // Si on a les deux en cache, afficher immédiatement
      if (cachedMeta && cachedContent) {
        console.log('⚡ Examen chargé depuis cache (instant)');
        setExamen(cachedMeta);
        setMarkdownContent(cachedContent);
        setIsFromCache(true);
        setLoading(false);
        return;
      }

      // Sinon, afficher le loader
      setLoading(true);

      // 2. Charger depuis Supabase
      const { data, error: fetchError } = await supabase
        .from('prof_kylian_examens')
        .select('*')
        .eq('id', id as string)
        .single();

      if (fetchError) {
        console.error('Erreur fetch examen:', fetchError);
        setError('Examen introuvable');
        setLoading(false);
        return;
      }

      if (!data) {
        setError('Examen introuvable');
        setLoading(false);
        return;
      }

      // Sauvegarder les métadonnées en cache
      await setCachedExamenMeta(data.id, data as ExamenItem);

      setExamen(data as ExamenItem);
      await loadMarkdownContent(data as ExamenItem);

    } catch (err) {
      console.error('Erreur chargement examen:', err);
      setError('Impossible de charger l\'examen.');
      setLoading(false);
    }
  };

  const loadMarkdownContent = async (examenData: ExamenItem) => {
    try {
      if (!examenData.corrige_url) {
        setError('Corrigé non disponible');
        setLoading(false);
        return;
      }

      // 1. Essayer le cache IMMÉDIATEMENT
      const cached = await getCachedExamen(examenData.id);
      
      if (cached) {
        console.log('📦 Examen chargé depuis le cache:', examenData.id);
        setMarkdownContent(cached);
        setIsFromCache(true);
        setLoading(false);
        
        // Rafraîchir en arrière-plan si le cache a plus de 24h
        const cacheData = await AsyncStorage.getItem(`${CACHE_PREFIX}${examenData.id}`);
        if (cacheData) {
          const { timestamp } = JSON.parse(cacheData);
          if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
            // Rafraîchir silencieusement en arrière-plan
            fetchMarkdownSilent(examenData);
          }
        }
        return;
      }
      
      // 2. Sinon, fetch depuis Supabase Storage
      console.log('🌐 Téléchargement de l\'examen:', examenData.id);
      await fetchMarkdown(examenData);
      
    } catch (err) {
      console.error('Erreur chargement markdown:', err);
      setError('Impossible de charger le contenu.');
      setLoading(false);
    }
  };

  // Fetch silencieux en arrière-plan (sans loader)
  const fetchMarkdownSilent = async (examenData: ExamenItem) => {
    try {
      if (!examenData.corrige_url) return;

      const response = await fetch(examenData.corrige_url);
      if (!response.ok) return;
      
      const text = await response.text();
      await setCachedExamen(examenData.id, text);
      
      // Mettre à jour silencieusement si le contenu a changé
      setMarkdownContent(text);
      setIsFromCache(false);
    } catch (err) {
      console.error('Erreur fetch silent:', err);
    }
  };

  const fetchMarkdown = async (examenData: ExamenItem) => {
    try {
      if (!examenData.corrige_url) {
        setError('Corrigé non disponible');
        setLoading(false);
        return;
      }

      const response = await fetch(examenData.corrige_url);
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }
      
      const text = await response.text();
      
      await setCachedExamen(examenData.id, text);
      
      setMarkdownContent(text);
      setIsFromCache(false);
      
    } catch (err) {
      console.error('Erreur fetch markdown:', err);
      setError('Impossible de charger le corrigé. Vérifie ta connexion.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    if (!examen) return;
    setRefreshing(true);
    setIsFromCache(false);
    await fetchMarkdown(examen);
  };

  const openSujetPDF = () => {
    if (examen?.pdf_url) {
      Linking.openURL(examen.pdf_url);
    }
  };

  const elements = parseMarkdown(markdownContent);

  const renderElement = (element: ParsedElement, index: number) => {
    switch (element.type) {
      case 'h1':
        return (
          <View key={index}>
            <View style={styles.h1Container}>
              <Text style={styles.h1}>{element.content}</Text>
            </View>
            {/* Badge Prof Kylian - sous le titre principal */}
            <View style={styles.profKylianBadge}>
              <Check size={14} color={colors.white} weight="bold" />
              <Text style={styles.profKylianBadgeText}>Méthode du Prof Kylian pour avoir 20/20</Text>
            </View>
          </View>
        );
      
      case 'h2':
        const isRetenir = element.content.includes('À retenir');
        return (
          <View key={index} style={[styles.h2Container, isRetenir && styles.h2RetenirContainer]}>
            {isRetenir && (
              <BookOpen size={22} color={colors.emerald600} weight="duotone" />
            )}
            <Text style={[styles.h2, isRetenir && styles.h2Retenir]}>{element.content}</Text>
          </View>
        );
      
      case 'h3':
        return <Text key={index} style={styles.h3}>{element.content}</Text>;
      
      case 'paragraph':
        return (
          <Text key={index} style={styles.paragraph}>
            {formatText(element.content).map((part, i) => (
              <Text key={i} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
            ))}
          </Text>
        );
      
      case 'quote':
        return (
          <View key={index} style={styles.quoteContainer}>
            <View style={styles.quoteLine} />
            <Text style={styles.quoteText}>
              {formatText(element.content).map((part, i) => (
                <Text key={i} style={part.bold ? styles.quoteBold : undefined}>{part.text}</Text>
              ))}
            </Text>
          </View>
        );
      
      case 'image':
        return (
          <TouchableOpacity 
            key={index} 
            style={styles.imageContainer}
            onPress={() => setZoomImage(element.content)}
            activeOpacity={0.8}
          >
            <Image 
              source={{ uri: element.content }} 
              style={styles.image}
              resizeMode="contain"
            />
            <Text style={styles.imageTapHint}>Appuie pour agrandir</Text>
          </TouchableOpacity>
        );
      
      case 'table':
        if (!element.rows || element.rows.length === 0) return null;
        const headers = element.rows[0];
        const dataRows = element.rows.slice(1);
        const colWidth = 100 / headers.length;
        
        return (
          <View key={index} style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              {headers.map((header, i) => (
                <View key={i} style={[styles.tableHeaderCell, { width: `${colWidth}%` }]}>
                  <Text style={styles.tableHeaderText}>{header}</Text>
                </View>
              ))}
            </View>
            {dataRows.map((row, rowIndex) => (
              <View key={rowIndex} style={[styles.tableRow, rowIndex % 2 === 1 && styles.tableRowAlt]}>
                {row.map((cell, cellIndex) => (
                  <View key={cellIndex} style={[styles.tableCell, { width: `${colWidth}%` }]}>
                    <Text style={styles.tableCellText}>
                      {formatText(cell).map((part, i) => (
                        <Text key={i} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                      ))}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        );
      
      case 'list':
        return (
          <View key={index} style={styles.listContainer}>
            {element.items?.map((item, i) => (
              <View key={i} style={styles.listItem}>
                <View style={styles.listBullet} />
                <Text style={styles.listText}>
                  {formatText(item).map((part, j) => (
                    <Text key={j} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                  ))}
                </Text>
              </View>
            ))}
          </View>
        );
      
      case 'numbered-list':
        return (
          <View key={index} style={styles.listContainer}>
            {element.items?.map((item, i) => (
              <View key={i} style={styles.listItem}>
                <Text style={styles.listNumber}>{i + 1}.</Text>
                <Text style={styles.listText}>
                  {formatText(item).map((part, j) => (
                    <Text key={j} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                  ))}
                </Text>
              </View>
            ))}
          </View>
        );
      
      case 'hr':
        return <View key={index} style={styles.hr} />;
      
      default:
        return null;
    }
  };

  // État de chargement
  if (loading) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.loadingContainer]}>
          <ActivityIndicator size="large" color={colors.indigo600} />
          <Text style={styles.loadingText}>Chargement du corrigé...</Text>
        </View>
      </>
    );
  }

  // État d'erreur
  if (error || !examen) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.errorContainer]}>
          <WifiSlash size={64} color={colors.slate300} weight="duotone" />
          <Text style={styles.errorTitle}>{error || 'Examen introuvable'}</Text>
          <Text style={styles.errorSubtitle}>
            Vérifie ta connexion internet et réessaie.
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadExamenFromSupabase}>
            <Text style={styles.retryButtonText}>Réessayer</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.errorButton} onPress={() => router.back()}>
            <Text style={styles.errorButtonText}>Retour</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        {/* Header - Même style violet que les fiches */}
        <LinearGradient
          colors={[colors.indigo700, colors.indigo600]}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <ArrowLeft size={24} color={colors.white} weight="bold" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle} numberOfLines={2}>{examen.title}</Text>
              <View style={styles.headerSubtitleRow}>
                <Text style={styles.headerChapter}>
                  {examen.matiere} • {examen.year}
                </Text>
                {isFromCache && (
                  <View style={styles.cacheBadge}>
                    <CloudCheck size={12} color={colors.emerald500} weight="fill" />
                    <Text style={styles.cacheBadgeText}>Hors-ligne</Text>
                  </View>
                )}
              </View>
            </View>
          </View>

          {/* Bouton Voir le sujet PDF */}
          {examen.pdf_url && (
            <TouchableOpacity style={styles.sujetButton} onPress={openSujetPDF}>
              <FileText size={20} color={colors.indigo600} weight="duotone" />
              <Text style={styles.sujetButtonText}>Voir le sujet officiel</Text>
              <ArrowSquareOut size={18} color={colors.indigo600} weight="bold" />
            </TouchableOpacity>
          )}
        </LinearGradient>

        {/* Contenu - Corrigé */}
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.indigo600]}
              tintColor={colors.indigo600}
            />
          }
        >
          {elements.length === 0 ? (
            <View style={styles.emptyContent}>
              <Text style={styles.emptyContentText}>Contenu en cours de chargement...</Text>
            </View>
          ) : (
            elements.map((element, index) => renderElement(element, index))
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </View>

      {/* Modal Zoom Image */}
      <Modal
        visible={zoomImage !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setZoomImage(null)}
      >
        <View style={styles.zoomModalContainer}>
          <TouchableOpacity 
            style={styles.zoomCloseButton}
            onPress={() => setZoomImage(null)}
          >
            <Text style={styles.zoomCloseText}>✕</Text>
          </TouchableOpacity>
          
          <ScrollView
            style={styles.zoomScrollView}
            contentContainerStyle={styles.zoomScrollContent}
            maximumZoomScale={3}
            minimumZoomScale={1}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            centerContent={true}
          >
            {zoomImage && (
              <Image
                source={{ uri: zoomImage }}
                style={styles.zoomImage}
                resizeMode="contain"
              />
            )}
          </ScrollView>
          
          <Text style={styles.zoomHint}>Pince pour zoomer • Appuie sur ✕ pour fermer</Text>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },

  // Header (même style violet que les fiches)
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  headerSubtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  headerChapter: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
  },
  cacheBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  cacheBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.emerald500,
  },

  // Bouton Sujet (couleur violette)
  sujetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: colors.white,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    marginTop: 16,
  },
  sujetButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.indigo600,
  },

  // Badge Prof Kylian - sous le titre H1
  profKylianBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 6,
    backgroundColor: colors.emerald500,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 12,
    marginBottom: 20,
  },
  profKylianBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 15,
    color: colors.slate500,
  },

  // Scroll content
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },

  // Empty content
  emptyContent: {
    padding: 40,
    alignItems: 'center',
  },
  emptyContentText: {
    fontSize: 15,
    color: colors.slate400,
  },

  // Titres avec fond violet
  h1Container: {
    marginBottom: 0,
    backgroundColor: colors.indigo600,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  h1: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.white,
    lineHeight: 32,
    textAlign: 'center',
  },
  h2Container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 28,
    marginBottom: 16,
    backgroundColor: colors.indigo600,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  h2RetenirContainer: {
    backgroundColor: colors.emerald500,
    padding: 12,
    borderRadius: 12,
    marginTop: 24,
  },
  h2: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
  },
  h2Retenir: {
    color: colors.white,
  },
  h3: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.white,
    backgroundColor: colors.indigo500,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 20,
    marginBottom: 12,
    overflow: 'hidden',
  },

  // Paragraphe
  paragraph: {
    fontSize: 16,
    color: colors.slate700,
    lineHeight: 26,
    marginBottom: 12,
  },
  bold: {
    fontWeight: '700',
    color: colors.slate900,
  },

  // Citation (violet)
  quoteContainer: {
    flexDirection: 'row',
    backgroundColor: colors.indigo50,
    borderRadius: 12,
    padding: 16,
    marginVertical: 12,
  },
  quoteLine: {
    width: 4,
    backgroundColor: colors.indigo600,
    borderRadius: 2,
    marginRight: 14,
  },
  quoteText: {
    flex: 1,
    fontSize: 15,
    color: colors.slate600,
    fontStyle: 'italic',
    lineHeight: 24,
  },
  quoteBold: {
    fontWeight: '700',
    color: colors.slate900,
    fontStyle: 'normal',
  },

  // Image
  imageContainer: {
    marginVertical: 20,
    alignItems: 'center',
    backgroundColor: colors.slate50,
    borderRadius: 16,
    padding: 16,
  },
  image: {
    width: width - 72,
    height: 220,
    borderRadius: 12,
  },
  imageTapHint: {
    marginTop: 8,
    fontSize: 12,
    color: colors.slate400,
    fontStyle: 'italic',
  },

  // Zoom Modal
  zoomModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomCloseButton: {
    position: 'absolute',
    top: 60,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  zoomCloseText: {
    fontSize: 24,
    color: colors.white,
    fontWeight: '600',
  },
  zoomScrollView: {
    flex: 1,
    width: '100%',
  },
  zoomScrollContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomImage: {
    width: width,
    height: height * 0.7,
  },
  zoomHint: {
    position: 'absolute',
    bottom: 50,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },

  // Table (violet)
  tableContainer: {
    marginVertical: 16,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.indigo600,
  },
  tableHeaderCell: {
    paddingVertical: 14,
    paddingHorizontal: 12,
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.white,
  },
  tableRow: {
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  tableRowAlt: {
    backgroundColor: colors.slate50,
  },
  tableCell: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRightWidth: 1,
    borderRightColor: colors.slate200,
  },
  tableCellText: {
    fontSize: 14,
    color: colors.slate700,
    lineHeight: 22,
  },

  // Liste (violet)
  listContainer: {
    marginVertical: 12,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  listBullet: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.indigo600,
    marginTop: 8,
    marginRight: 12,
  },
  listNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.indigo600,
    marginRight: 10,
    minWidth: 24,
  },
  listText: {
    flex: 1,
    fontSize: 16,
    color: colors.slate700,
    lineHeight: 26,
  },

  // Séparateur
  hr: {
    height: 2,
    backgroundColor: colors.slate200,
    marginVertical: 24,
    borderRadius: 1,
  },

  // Error state
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    marginTop: 20,
  },
  errorSubtitle: {
    fontSize: 15,
    color: colors.slate500,
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 24,
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  errorButton: {
    marginTop: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  errorButtonText: {
    color: colors.slate500,
    fontSize: 16,
    fontWeight: '600',
  },
});
